import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { AriaScreen } from "@/components/app/assistant/chat/aria-screen";
import { keepsEscape } from "@/components/app/assistant/chat/panel-layout";
import { useAriaScreen } from "@/components/app/assistant/chat/use-aria-screen";
import { usePolicy } from "@/lib/policy";
import { guardFeature } from "@/lib/policy/route-guard";
import { chooseWorkspace } from "@/lib/workspace/workspace";
import { lastPlaces } from "@/lib/workspace/workspace-memory";

export const Route = createFileRoute("/_authenticated/aria/")({
  validateSearch: (search: Record<string, unknown>): { c?: string } =>
    typeof search.c === "string" && search.c ? { c: search.c } : {},
  beforeLoad: ({ context }) => guardFeature(context.me, "ai-assistant"),
  component: AriaPage,
});

/** The same `AriaScreen` as `AriaPanel`, so everything Aria learns in Core works here. `c` in the
 * address is the conversation; without one, or naming one from another community, every arrival
 * opens a new conversation. */
function AriaPage() {
  const { c } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { me } = usePolicy();
  /* Arriving without a conversation in the address is a launch, and a launch
     starts a new conversation. */
  const [fresh] = useState(() => !c);
  const screen = useAriaScreen({ open: true, fresh });
  const { sessionId, sessions, select, relaunch } = screen.session;

  /* Coming back to the bare address while already here (the "New
     conversation" shortcut) is a launch too. */
  const shown = useRef(c);
  useEffect(() => {
    const before = shown.current;
    shown.current = c;
    if (before && !c) relaunch();
  }, [c, relaunch]);

  /* The address names the conversation: a link picks it, a pick moves it.
     Until a linked conversation has been picked the address is left alone, or
     a cached list would rewrite it to the newest thread first. */
  const linked = useRef(c ?? null);
  useEffect(() => {
    if (linked.current) select(linked.current);
  }, [select]);
  useEffect(() => {
    const want = linked.current;
    if (want && sessionId !== want && sessions.some((row) => row.id === want)) return;
    linked.current = null;
    if (sessionId && sessionId !== c) void navigate({ search: { c: sessionId }, replace: true });
  }, [c, navigate, sessionId, sessions]);

  /* Escape during a recording discards it, as in Core's panel. */
  const { capturing, cancelDictation } = screen.composer;
  useEffect(() => {
    if (!capturing) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || keepsEscape(event.target)) return;
      event.preventDefault();
      cancelDictation();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [capturing, cancelDictation]);

  const follow = useCallback((href: string) => void navigate({ to: href }), [navigate]);
  /* Closing Aria is going back to the building, where it was left. */
  const leave = useCallback(() => {
    chooseWorkspace("core", me.user.id);
    void navigate({ to: lastPlaces(me.user.id).core ?? "/" });
  }, [me.user.id, navigate]);

  return (
    <AriaScreen
      screen={screen}
      follow={follow}
      onClose={leave}
      /* `window-screen`: on a phone it takes the bars' room while they are tucked away. */
      className="window-screen flex h-[calc(100svh-3.5rem-var(--dock-h,0px)-var(--open-app-h,0px))] min-h-0 w-full"
      guardDrops
      heading={<h1 className="sr-only">Aria</h1>}
    />
  );
}
